import { createPortal } from 'react-dom'
import { Loader2 } from 'lucide-react'
import {
  JSX,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
  type RefObject,
} from 'react'
import { z } from 'zod'
import {
  useForm as useReactHookForm,
  FormProvider,
  useFormContext as useReactHookFormContext,
  useWatch,
} from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import { Button } from '@/shared/components/ui/button'
import { toastApiError } from '@/shared/lib/api-error'
import { useFormattingContext } from '@/shared/lib/formatting'
import { GeneratedInput } from './generated-input'
import { getFormValidation, normaliseCheckboxValue } from './generated-helpers'
import { isFieldVisible } from './normalise'
import { RenderSettings } from './render-settings'
import { makeFormContext, UploadProvider } from './form-context'
import type { FormField, Setting, FormContext } from './generated-types'

export interface FormPropsConfig {
  name: string
  submit?:
    | 'default'
    | 'footer'
    | string
    | ((ctx: FormContext) => JSX.Element)
  settings?: Setting[]
  portalSelector?: string
  fields: FormField[]
  uploadFile?: (args: {
    file: File
    use_case: string
    /** Registry / form field code when the uploader is field-scoped (IRIS). */
    fieldName?: string
  }) => Promise<{ uuid?: string; UUID?: string; file_name?: string; fileName?: string } & Record<string, unknown>>
}

type SubmitHandler = (
  values: Record<string, unknown>,
  ctx: FormContext,
  uploads?: Record<string, Record<string, unknown>>,
) => Promise<unknown> | unknown

type GeneratedUseFormReturn = {
  Form: ({
    children,
    className,
    onCancel,
    submit,
  }: {
    children?: ReactNode | (({ formContext }: { formContext: FormContext }) => ReactNode)
    className?: string
    onCancel?: () => void
    submit?: string
  }) => JSX.Element
  FormFooter?: ({
    onCancel,
    submitText,
    disabled,
  }: {
    onCancel?: () => void
    submitText?: string
    disabled?: boolean
  }) => JSX.Element
  ref: RefObject<FormContext | null>
}

function getFieldName(field: FormField): string {
  if ('name' in field && field.name) return field.name
  if ('label' in field && field.label) return field.label.replace(/ /g, '_').toLowerCase()
  throw new Error("FormField must have either a 'name' or 'label' property")
}

function toSnakeCase(value: string): string {
  return value.replace(/[\s-]/g, '_').toLowerCase()
}

// Internal sentinel for the `submit` discriminant when a caller passes a
// render-function. Kept collision-proof so it can never be mistaken for a
// caller-supplied button label string.
const CUSTOM_SUBMIT = '__raven_custom_submit__'

// Build a value-based cache key for a field config array. Functions
// (onChange / render / visible) and React elements (leadingIcon, custom
// nodes) are skipped: their identity churns every render but never affects
// the field's *rendered structure*, so including them would defeat the
// memo and remount inputs on every keystroke. Everything that does affect
// rendering - labels, options, types, required, placeholders - is a
// serialisable primitive and is captured. This lets callers pass an inline
// `fields={[...]}` literal without `useMemo` and still keep `Form` stable.
function stableFieldsKey(fields: FormField[]): string {
  return JSON.stringify(fields, (_key, value) => {
    if (typeof value === 'function') return undefined
    if (value && typeof value === 'object' && '$$typeof' in value) return undefined
    return value
  })
}

// A field hides itself either imperatively (`visible` boolean/predicate) or
// declaratively (`configuration_data.visible_when`). A field the user cannot
// currently see must NOT block submit - otherwise a required-but-hidden field
// (e.g. `duration_in_months` while "Lifetime" is checked) fails validation
// against its empty default and the form silently refuses to submit with no
// visible error. This mirrors GeneratedInput's visibility gate exactly; keep
// the two in lockstep.
function isFieldCurrentlyHidden(
  field: FormField & { configurationData?: Record<string, unknown> },
  values: Record<string, unknown>,
): boolean {
  if (typeof field.visible === 'boolean' && !field.visible) return true
  if (typeof field.visible === 'function') {
    const ctx = {
      values,
      errors: {},
      isValid: true,
      isSubmitting: false,
      setFieldValue: () => {},
      setFieldError: () => {},
      setValues: () => {},
      submitForm: async () => {},
      validateForm: async () => ({}),
      resetForm: () => {},
    } as FormContext
    if (!field.visible(ctx)) return true
  }
  if (
    field.configurationData?.visible_when &&
    !isFieldVisible(field.configurationData, values)
  ) {
    return true
  }
  return false
}

/**
 * Dev-only guard for the `type:'custom'` deps trap.
 *
 * `stableFieldsKey` and `depsKey` both go through JSON, which drops function
 * properties. A custom field's `render` is a closure, so it is invisible to
 * both keys: if the closure reads component state, that state MUST be listed
 * in `deps` by name or the memo hands back the previous closure and the
 * control silently stops responding. It still renders - it just renders the
 * values it captured on the render that first built it.
 *
 * Passing the `fields` array itself is the specific mistake this catches. It
 * looks like it should work and does nothing at all, because `fields`
 * serialises identically whichever state changed. That has now shipped three
 * times: the Smart Schedule Options fold, the geography scope chips, and the
 * Assign/Move field-tech picker, where it made the input impossible to type
 * in. Each was found by a person noticing a dead control, which is the worst
 * way to find it.
 *
 * Warns rather than throws - a false positive must never take a form down.
 */
function useCustomFieldDepsGuard(
  config: FormPropsConfig,
  deps: unknown[],
): void {
  if (!import.meta.env?.DEV) return
  const hasCustomRender = (config.fields ?? []).some(
    (f) => f?.type === 'custom' && typeof (f as { render?: unknown }).render === 'function',
  )
  if (!hasCustomRender) return

  // Did the caller pass the field config array itself? Anything that is an
  // array of objects carrying a `name` and a `type` is the `fields` array.
  const passedFieldsArray = deps.some(
    (d) =>
      Array.isArray(d) &&
      d.length > 0 &&
      d.every(
        (x) =>
          !!x &&
          typeof x === 'object' &&
          'name' in (x as object) &&
          'type' in (x as object),
      ),
  )
  const onlyStableDeps = deps.every(
    (d) => d == null || ['string', 'number', 'boolean'].includes(typeof d),
  )

  if (passedFieldsArray) {
    console.warn(
      `[useForm:${config.name}] deps includes the \`fields\` array, which does ` +
        'nothing - deps are compared with JSON.stringify and field configs ' +
        'serialise identically whichever state changed. A `type:"custom"` ' +
        'render closure will go stale and its control will stop responding. ' +
        'List the state the closure reads (search text, results, labels) by ' +
        'name instead.',
    )
  } else if (deps.length === 0 || onlyStableDeps) {
    console.warn(
      `[useForm:${config.name}] has a \`type:"custom"\` field but deps carry ` +
        'no component state. If that render closure reads state, list it in ' +
        'deps - otherwise the control renders the values it captured first ' +
        'and silently ignores later changes.',
    )
  }
}

function useGeneratedFormInternal(
  config: FormPropsConfig,
  submitCb: SubmitHandler,
  deps: unknown[] = [],
): GeneratedUseFormReturn {
  const { defaultCountryCode, currency: defaultCurrency } = useFormattingContext()
  // Serialize deps to a stable string so useMemo comparisons use value
  // equality instead of reference equality - a new array literal with
  // the same elements must NOT bust the cache and recreate Form.
  const depsKey = JSON.stringify(deps)
  useCustomFieldDepsGuard(config, deps)
  const configName = config.name
  const portalSelector = config.portalSelector
  // Stabilise `settings` by value so a fresh array literal with the same
  // contents does not bust the `Form` memo and remount every input.
  // `settingsKey` (JSON) cannot see through `onChange`/`content` closures, so
  // `depsKey` is included as the escape hatch: a caller whose setting handlers
  // close over changing state passes that state in `deps` to force a refresh.
  const settingsKey = JSON.stringify(config.settings ?? null)
  const settings = useMemo(() => config.settings, [settingsKey, depsKey]) // eslint-disable-line react-hooks/exhaustive-deps
  // Value-key the field config so a fresh `fields` array literal with the
  // same contents does NOT recompute `processedFields` (and thereby churn
  // `Form` and remount every input). This removes the last caller-discipline
  // requirement behind the recurring focus-loss bug.
  const fieldsKey = stableFieldsKey(config.fields)
  // `submit` may be a render-function (callers pass an inline `() => ...` to
  // suppress/replace the default footer). An inline function is a fresh
  // reference every render, so depending on it directly churns `Form`'s
  // identity and remounts every field (focus loss). Hold the function behind
  // a ref and expose a stable wrapper; for the string forms the value is
  // already stable. This is the once-for-all-callers fix for the recurring
  // "input loses focus on each keystroke" bug.
  // A function-typed `submit` is treated as a static footer-suppressor /
  // simple custom-submit renderer. It is snapshotted via a ref so it never
  // churns `Form`'s identity. NOTE: because the ref refreshes in an effect,
  // the function is read one render behind - so a `submit` fn that closes over
  // rapidly-changing state must NOT be used. Render such submit UI as `Form`
  // children instead (fresh on every render); see partnership-invite-page.
  const submitFnRef = useRef(typeof config.submit === 'function' ? config.submit : null)
  useEffect(() => {
    submitFnRef.current = typeof config.submit === 'function' ? config.submit : null
  })
  // `submitConfig` is a STABLE discriminant only - never the raw function -
  // so it is safe in the `Form`/`FormFooter` memo deps and comparisons. A
  // function-typed `submit` collapses to the `'custom'` mode; the actual
  // render function is invoked in `FormBody` via the ref above.
  const submitConfig: string =
    typeof config.submit === 'function' ? CUSTOM_SUBMIT : (config.submit ?? 'default')
  const renderCustomSubmit = useCallback(
    (ctx: FormContext) => submitFnRef.current?.(ctx) ?? null,
    [],
  )

  const processedFields = useMemo(() => {
    void depsKey
    return config.fields.map((field) => ({
      ...field,
      country: defaultCountryCode,
      currency:
        ('value' in field && typeof field.value === 'object' && field.value && 'currency' in (field.value as Record<string, unknown>))
          ? (field.value as { currency?: string }).currency
          : defaultCurrency,
      name: getFieldName(field),
      validationSchema: field.validationSchema || getFormValidation(field),
    }))
    // `config.fields` is intentionally keyed by value (`fieldsKey`), not
    // identity, so an unmemoised caller array does not bust this cache.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldsKey, defaultCountryCode, defaultCurrency, depsKey])

  const initialValues = useMemo(
    () => {
      void depsKey
      return !config.settings
        ? processedFields.reduce<Record<string, unknown>>((acc, field) => {
            let defaultValue = field.value

            if (field.type === 'incremental') {
              defaultValue = field.value || []
            } else if (field.type === 'file' || field.type === 'picture') {
              defaultValue = field.value ?? null
            } else if (
              field.type === 'checkbox' ||
              field.type === 'switch' ||
              field.type === 'disclaimer'
            ) {
              // Both validate as ``boolean | null``, and callers seed an
              // unanswered attribute as ``''`` - which ``?? false`` let through.
              defaultValue = normaliseCheckboxValue(field.value)
            } else if (field.type === 'entity-search') {
              // Entity-search validates as ``object | null``. Falling through
              // to ``field.value || ''`` would seed an empty string, which the
              // object validator rejects on submit even when the field is
              // optional and untouched.
              defaultValue =
                field.value && typeof field.value === 'object'
                  ? field.value
                  : null
            } else if (field.isMulti || field.type === 'multiSelect') {
              // Multi-selects validate as ``z.array(z.string())``. Falling
              // through to ``field.value || ''`` would seed an empty string,
              // which the array validator rejects on submit even when the
              // field is optional and untouched.
              defaultValue = Array.isArray(field.value) ? field.value : []
            } else if (typeof field.value === 'boolean') {
              // Custom fields can hold a boolean (e.g. a toggle button). Keep
              // it as-is - ``field.value || ''`` would mangle ``false`` into
              // an empty string and break boolean submission downstream.
              defaultValue = field.value
            } else if (typeof field.value === 'number') {
              // Numeric fields (e.g. a NUMBER attribute like ``priority``) keep
              // their value as-is - ``field.value || ''`` would turn a genuine
              // ``0`` into an empty string, blanking the input.
              defaultValue = field.value
            } else {
              defaultValue = field.value || ''
            }

            if (field.type === 'currency') {
              defaultValue = field.value || {
                amount: undefined,
                currency:
                  defaultCurrency ||
                  (Array.isArray(field.options) && field.options.length > 0 ? field.options[0].value : ''),
              }
            }

            if (
              field.type === 'address' &&
              (!defaultValue || typeof defaultValue !== 'object')
            ) {
              defaultValue = {
                addressLine1: '',
                addressLine2: '',
                locality: '',
                region: '',
                postalCode: '',
                countryCode: defaultCountryCode || '',
              }
            }

            if ((field.name === 'country' || field.label?.match(/country/i)) && !field.value) {
              defaultValue = defaultCountryCode || ''
            }

            acc[field.name] = defaultValue
            return acc
          }, {})
        : config.settings.reduce<Record<string, unknown>>((acc, field) => {
            const fieldName = field.key || toSnakeCase(field.title)
            acc[fieldName] = field.value
            return acc
          }, {})
    },
    [config.settings, processedFields, defaultCurrency, defaultCountryCode, depsKey],
  )

  const validationSchema = useMemo(() => {
    void depsKey
    return z
      .object(
        processedFields.reduce<Record<string, z.ZodTypeAny>>((acc, field) => {
          acc[field.name] = field.validationSchema as z.ZodTypeAny
          return acc
        }, {}),
      )
      .passthrough()
  }, [processedFields, depsKey])

  const processedFieldsRef = useRef(processedFields)
  useEffect(() => {
    processedFieldsRef.current = processedFields
  }, [processedFields])

  // Wrap the zod resolver so a currently-hidden field can never block submit.
  // The zod schema is built from ALL fields (it must be - which fields are
  // visible changes with form values), so a required-but-hidden field (e.g.
  // `duration_in_months` while "Lifetime" is checked) would otherwise fail
  // validation against its empty default.
  //
  // We must NOT fix this by deleting the hidden field's error after the fact:
  // react-hook-form's zodResolver returns `values: {}` (empty) whenever parsing
  // produces ANY error, so clearing the last error would let RHF submit with
  // every field blanked. Instead we swap each hidden field's schema to
  // `z.any()` at resolve time, so parsing succeeds and zodResolver returns the
  // full, coerced values for the visible fields.
  const baseResolver = useMemo(
    () =>
      zodResolver(validationSchema as never) as (
        values: Record<string, unknown>,
        context: unknown,
        options: unknown,
      ) => Promise<{ values: Record<string, unknown>; errors: Record<string, unknown> }>,
    [validationSchema],
  )

  const resolver = useMemo(() => {
    return async (values: Record<string, unknown>, context: unknown, options: unknown) => {
      const hidden = processedFields.filter((field) => isFieldCurrentlyHidden(field, values))
      if (hidden.length === 0) {
        return baseResolver(values, context, options)
      }
      const hiddenNames = new Set(hidden.map((field) => field.name as string))
      const scopedSchema = z
        .object(
          processedFields.reduce<Record<string, z.ZodTypeAny>>((acc, field) => {
            const name = field.name as string
            acc[name] = hiddenNames.has(name)
              ? z.any()
              : (field.validationSchema as z.ZodTypeAny)
            return acc
          }, {}),
        )
        .passthrough()
      const scoped = zodResolver(scopedSchema as never) as typeof baseResolver
      return scoped(values, context, options)
    }
  }, [baseResolver, processedFields])

  const methods = useReactHookForm<Record<string, unknown>>({
    defaultValues: initialValues,
    resolver: resolver as never,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  })

  // Reinitialise when the computed initial values genuinely change, WITHOUT
  // wiping validation errors or the user's in-progress edits. This is the
  // crucial behaviour that React Hook Form makes safe (`keepErrors` /
  // `keepDirtyValues`) and the previous form library did not - an externally-triggered submit
  // can leave inline errors on screen and a reinit won't clear them.
  const initialJson = JSON.stringify(initialValues)
  const prevInitialRef = useRef(initialJson)
  useEffect(() => {
    if (prevInitialRef.current === initialJson) return
    prevInitialRef.current = initialJson
    methods.reset(initialValues, { keepErrors: true, keepDirtyValues: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialJson])

  const submitCbRef = useRef(submitCb)
  useEffect(() => {
    submitCbRef.current = submitCb
  })

  // Keep the caller's `uploadFile` behind a ref so neither `runSubmit` nor
  // `Form` depend on its (usually inline, per-render) identity. Callers must
  // not have to remember to `useCallback` it - the latent remount/focus-loss
  // bug that recurs otherwise is fixed here, once, for every caller.
  const uploadFileRef = useRef(config.uploadFile)
  useEffect(() => {
    uploadFileRef.current = config.uploadFile
  })
  // A stable wrapper handed to the UploadProvider; null when no uploader is
  // configured so file inputs can still tell "uploads unavailable" apart.
  const hasUploader = Boolean(config.uploadFile)
  const uploadFile = useMemo<NonNullable<FormPropsConfig['uploadFile']> | null>(
    () => (hasUploader ? (args) => uploadFileRef.current!(args) : null),
    [hasUploader],
  )

  // The neutral controller exposed via `ref`. `values`/`errors` are read
  // imperatively (snapshot) which suits ref consumers.
  const controllerRef = useRef<FormContext | null>(null)

  const runSubmit = useCallback(async () => {
    await methods.handleSubmit(async (values) => {
      const uploads: Record<string, Record<string, unknown>> = {}
      try {
        const files: Record<string, File> = {}
        Object.keys(values).forEach((key) => {
          if (values[key] instanceof File) {
            files[key] = values[key] as File
          }
          const current = values[key] as Record<string, unknown> | undefined
          if (current && typeof current === 'object' && ('fileName' in current || 'file_name' in current)) {
            uploads[key] = current
            values[key] = (current.UUID ?? current.uuid) as unknown
          } else if (current && typeof current === 'object' && 'display' in current && typeof current.value === 'object') {
            const nested = current.value as Record<string, unknown>
            if (nested?.uuid) {
              uploads[key] = current
              values[key] = nested.uuid as unknown
            }
          }
        })

        if (uploadFileRef.current) {
          try {
            await Promise.all(
              Object.keys(files).map(async (key) => {
                const uploaded = await uploadFileRef.current?.({
                  file: files[key],
                  use_case:
                    (processedFieldsRef.current.find((field) => field.name === key)?.useCase as string | undefined) ||
                    'PROOF_OF_PURCHASE',
                })
                if (uploaded) {
                  values[key] = (uploaded.UUID ?? uploaded.uuid) as unknown
                  uploads[key] = uploaded
                  methods.setValue(key, uploaded as never)
                }
              }),
            )
          } catch (error) {
            // The photo upload happens here. A silent failure used to leave the
            // wizard looking dead ("submit does nothing"); surface the backend
            // reason (size/format) before aborting so isSubmitting resets.
            toastApiError(error, 'Upload failed - please try again')
            throw error
          }
        }

        await submitCbRef.current(values, controllerRef.current as FormContext, uploads)
      } catch (error) {
        console.error(error)
      }
    })()
    // Reads caller-supplied callbacks via refs - stable identity by design.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Keep the neutral controller (exposed via `ref`) fresh each commit.
  // Assigned in an effect rather than during render so a ref write never
  // happens in the render phase. `values` is exposed as a live getter so
  // off-render reads (e.g. persisting on Back/unmount) see the latest typed
  // values, not the snapshot from the last commit (the form intentionally
  // does not re-render on every keystroke).
  useEffect(() => {
    const controller = makeFormContext(methods, methods.getValues(), runSubmit)
    Object.defineProperty(controller, 'values', {
      configurable: true,
      get: () => methods.getValues() as Record<string, unknown>,
    })
    controllerRef.current = controller
  })

  const Form = useCallback(
    ({
      children,
      className,
      onCancel,
      submit,
    }: {
      children?: ReactNode | (({ formContext }: { formContext: FormContext }) => ReactNode)
      className?: string
      onCancel?: () => void
      submit?: string
    }) => {
      const rendered = (
        <FormProvider {...methods}>
          <UploadProvider value={uploadFile ?? null}>
            <FormBody
              configName={configName}
              className={className}
              onCancel={onCancel}
              submit={submit}
              submitConfig={submitConfig}
              renderCustomSubmit={renderCustomSubmit}
              settings={settings}
              fields={processedFields}
              runSubmit={runSubmit}
            >
              {children}
            </FormBody>
          </UploadProvider>
        </FormProvider>
      )

      if (portalSelector) {
        const portalRoot = document.querySelector(portalSelector) || document.body
        return createPortal(rendered, portalRoot)
      }
      return rendered
    },
    [configName, portalSelector, settings, submitConfig, renderCustomSubmit, runSubmit, methods, processedFields, uploadFile],
  )

  const FormFooter = useMemo(
    () =>
      submitConfig === 'footer'
        ? ({
            onCancel,
            submitText,
            disabled,
          }: {
            onCancel?: () => void
            submitText?: string
            // When a save is in flight, callers pass `disabled` so neither
            // button (which live outside the `<Form />` `isSubmitting` overlay)
            // can trigger a duplicate submit or reset mid-save.
            disabled?: boolean
          }) => (
            <>
              <Button
                variant="outline"
                type="button"
                onClick={onCancel}
                disabled={disabled}
                data-testid="form-cancel-button"
              >
                Cancel
              </Button>
              {/* Submits THIS instance, not "the form with this id": a layout
                  that mounts a page twice (settings renders a desktop and a
                  mobile copy) has two forms sharing the id, and the browser
                  would submit the hidden one with its untouched values. */}
              <Button
                type="button"
                disabled={disabled}
                data-testid="form-submit-button"
                onClick={() => void runSubmit()}
              >
                {submitText || 'Save changes'}
              </Button>
            </>
          )
        : undefined,
    [submitConfig, runSubmit],
  )

  return submitConfig === 'footer'
    ? { Form, FormFooter, ref: controllerRef }
    : { Form, ref: controllerRef }
}

/**
 * Inner form body - lives inside the RHF `FormProvider` so it can read a
 * reactive value snapshot (`useWatch`) and build the neutral
 * `FormContext` passed to every field. Re-rendering on value changes
 * mirrors the previous behaviour where field callbacks and `visible`
 * predicates always saw current values.
 *
 * Internal helper co-located with the `useForm` hook; not a Fast-Refresh
 * boundary (the file's public export is the hook, not this component).
 */
// eslint-disable-next-line react-refresh/only-export-components
function FormBody({
  configName,
  className,
  onCancel,
  submit,
  submitConfig,
  renderCustomSubmit,
  settings,
  fields,
  runSubmit,
  children,
}: {
  configName: string
  className?: string
  onCancel?: () => void
  submit?: string
  submitConfig: string
  renderCustomSubmit: (ctx: FormContext) => ReactNode
  settings?: Setting[]
  fields: FormField[]
  runSubmit: () => Promise<void>
  children?: ReactNode | (({ formContext }: { formContext: FormContext }) => ReactNode)
}) {
  const methods = useReactHookFormContext<Record<string, unknown>>()
  const watched = (useWatch({ control: methods.control }) as Record<string, unknown>) ?? {}
  const formContext = makeFormContext(methods, watched, runSubmit)

  return (
    <form
      id={configName}
      className="w-full"
      noValidate
      onSubmit={(event) => {
        event.preventDefault()
        event.stopPropagation()
        void runSubmit()
      }}
    >
      <div className={className || 'grid grid-cols-2 gap-4'}>
        {methods.formState.isSubmitting && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-[var(--overlay-light)]">
            <Loader2 className="h-5 w-5 animate-spin text-[var(--green)]" />
          </div>
        )}
        {fields.map((field, index) => (
          <GeneratedInput
            key={`${field.name ?? field.label}-form-field`}
            field={field}
            index={index + 1}
            context={{ uploadHandlers: {}, formContext }}
          />
        ))}
        {fields.length === 0 &&
          settings?.map((setting) => (
            <RenderSettings
              setting={setting}
              key={setting.key || setting.title}
              formContext={formContext}
            />
          ))}
        {submitConfig === CUSTOM_SUBMIT && renderCustomSubmit(formContext)}
      </div>
      {typeof children === 'function' ? children({ formContext }) : children}
      {submitConfig !== 'footer' && submitConfig !== CUSTOM_SUBMIT && (
        <div className={`grid grid-cols-2 gap-4 border-t-[0.5px] border-[var(--rule)] px-5 py-4 ${className || ''}`}>
          <Button variant="outline" type="button" onClick={onCancel} data-testid="form-cancel-button">
            Cancel
          </Button>
          <Button type="submit" data-testid="form-submit-button">
            {submitConfig === 'default' ? submit || 'Save changes' : submitConfig}
          </Button>
        </div>
      )}
    </form>
  )
}

export default function useForm(
  config: FormPropsConfig,
  submitCb: SubmitHandler,
  deps?: unknown[],
): GeneratedUseFormReturn
export default function useForm(
  config: FormPropsConfig,
  submitCb?: SubmitHandler,
  deps: unknown[] = [],
): GeneratedUseFormReturn {
  return useGeneratedFormInternal(config, submitCb ?? (async () => undefined), deps)
}
